import { DismissButton, Icon, IconButton } from '@ps/isp-web-component-library';
import { CloseMedium } from '@ps/isp-web-component-library/icons/CloseMedium';
import { TickMedium } from '@ps/isp-web-component-library/icons/TickMedium';
import { Size as ScreenSize } from '@ps/web-core-enums';
import { useScreenSize } from '@ps/web-shared-components';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import { type Dispatch, select } from '@swf/core/store';
import { BET_SLIP_AUTO_DISMISS_DELAY } from '@swf/sports/utils/constants';

import { TXTWCL_Close } from './Header.messages.json';
import styles from './Header.scss';

export interface Props {
  title: React.ReactElement;
}

const Header: React.FC<Props> = ({ title }) => {
  const { betSlip } = useDispatch<Dispatch>();
  const { formatMessage } = useRegulatoryIntl();
  const isBetSlipOpen = useSelector(select.betSlip.isOpen);
  const { greaterThan } = useScreenSize();
  const isMobile = !greaterThan(ScreenSize.Small);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const displayDismissButton = isMobile && !isBetSlipOpen;

  useEffect(() => {
    if (!isMobile || isBetSlipOpen) {
      return;
    }

    let timer = timerRef.current;

    timer = setTimeout(() => {
      betSlip.receiptClosed();
      betSlip.betSlipOpened(false);
    }, BET_SLIP_AUTO_DISMISS_DELAY);

    return () => {
      clearTimeout(timer);
    };
  }, [isMobile, isBetSlipOpen, betSlip]);

  const handleReceiptHeaderClose = () => {
    betSlip.receiptClosed();
    betSlip.betSlipOpened(false);

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  };

  return (
    <div className={styles.header}>
      <span className={styles.tickIcon}>
        <Icon icon={TickMedium} size="xs" />
      </span>
      <h2>{title}</h2>
      {displayDismissButton ? (
        <DismissButton
          onDismiss={handleReceiptHeaderClose}
          dismissLabel={formatMessage(TXTWCL_Close)}
          variant="secondary"
          isAutoDismissible
        />
      ) : (
        <IconButton
          size="medium"
          onClick={handleReceiptHeaderClose}
          label={formatMessage(TXTWCL_Close)}
          icon={CloseMedium}
        />
      )}
    </div>
  );
};

export default Header;
